import React, { useState, useContext, useEffect } from "react";
import Swal from "sweetalert2";
import AuthService from "../../../services/Admin/AuthService";
import UserService from "../../../services/Admin/UserService";
import PackageService from "../../../services/Admin/PackageService";
import HttpError from "../../../utill/HttpError";
import LoadingContext from "../../../store/loading-context";
import { states } from "../../../utill/Common";

const EditUserForm = (props) => {
  const loadingCtx = useContext(LoadingContext);
  const [loading, setLoading] = useState(false);
  const [formChanged, setFormChanged] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    // Basic Info
    user_type: "",
    domain_name: "",
    package_id: "",
    name: "",
    fathers_name: "",
    mothers_name: "",
    gender: "",
    dob: "",
    mobile: "",
    email: "",
    alternative_number: "",
    whatsapp_number: "",
    pincode: "",
    state: "",
    city: "",
    district: "",
    address: "",

    // Shop Info
    shop_name: "",
    shop_type: "",
    nature_of_business: "",
    website_url: "",
    shop_pincode: "",
    shop_state: "",
    shop_city: "",
    shop_district: "",
    shop_address: "",
    shop_latitude: "",
    shop_longitude: "",

    // Bank Info
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    account_type: "",
    pob_type: "",

    // KYC Info
    pan_number: "",
    business_pan: "",
    gst_number: "",
    poa_id_number: "",

    // Status
    status: "",
    kyc_status: "",
    two_fa_status: "",
    wallet_hold: "",
    developer_access: "",

    nsdlpan_status: "",
    utiitslpan_status: "",
  });

  // Document states
  const [documents, setDocuments] = useState({});
  const [documentUrls, setDocumentUrls] = useState({});
  const [packageList, setPackageList] = useState([]);
  const [userData, setUserData] = useState(null);

  // Configuration
  const DOCUMENT_CONFIG = {
    profile_img: {
      label: "Profile Photo",
      description: "User profile photo",
      accept: ".jpg,.jpeg,.png",
    },
    poa_frontside_img: {
      label: "Aadhaar Front Side",
      description: "Aadhaar card front side",
      accept: ".jpg,.jpeg,.png",
    },
    poa_backside_img: {
      label: "Aadhaar Back Side",
      description: "Aadhaar card back side",
      accept: ".jpg,.jpeg,.png",
    },
    pan_img: {
      label: "PAN Card",
      description: "PAN card image",
      accept: ".jpg,.jpeg,.png",
    },
    gst_img: {
      label: "GST Certificate",
      description: "GST registration certificate",
      accept: ".jpg,.jpeg,.png,.pdf",
    },
    business_pan_img: {
      label: "Business PAN",
      description: "Business PAN card (if applicable)",
      accept: ".jpg,.jpeg,.png",
    },
    pob_img: {
      label: "Proof of Bank",
      description: "Bank passbook or cancelled cheque",
      accept: ".jpg,.jpeg,.png,.pdf",
    },
  };

  // Shop Type Options
  const SHOP_TYPE_OPTIONS = [
    { value: "", label: "Select Business  Type" },
    { value: "RETAIL", label: "Retail" },
    { value: "WHOLESALE", label: "Wholesale" },
    { value: "ONLINE", label: "Online" },
    { value: "SERVICE", label: "Service" },
    { value: "MANUFACTURING", label: "Manufacturing" },
    { value: "DISTRIBUTION", label: "Distribution" },
    { value: "FRANCHISE", label: "Franchise" },
    { value: "OTHER", label: "Other" },
  ];

  const GENDER_OPTIONS = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Transgender", label: "Transgender" },
  ];

  const USER_TYPE_OPTIONS = [
    "Retailer",
    "Distributor",
    "Master Distributor",
    "Reseller",
    "Master Reseller",
  ];

  const STATUS_OPTIONS = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
    { value: "Suspended", label: "Suspended" },
  ];

  const KYC_STATUS_OPTIONS = [
    { value: "Not Applied", label: "Not Applied" },
    { value: "Pending", label: "Pending" },
    { value: "Verified", label: "Verified" },
    { value: "Rejected", label: "Rejected" },
    { value: "Suspended", label: "Suspended" },
  ];

  const ACCOUNT_TYPE_OPTIONS = [
    { value: "SAVINGS", label: "Savings Account" },
    { value: "CURRENT", label: "Current Account" },
    { value: "SALARY", label: "Salary Account" },
  ];

  const POB_TYPE_OPTIONS = [
    { value: "", label: "Select Type" },
    { value: "Bank Passbook", label: "Bank Passbook" },
    { value: "Cancelled Cheque", label: "Cancelled Cheque" },
  ];

  const TWO_FA_OPTIONS = [
    { value: "ENABLED", label: "Enabled" },
    { value: "DISABLED", label: "Disabled" },
  ];

  const WALLET_HOLD_OPTIONS = [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
  ];

  const DEVELOPER_ACCESS_OPTIONS = [
    { value: "Enabled", label: "Enabled" },
    { value: "Disabled", label: "Disabled" },
  ];

  const SERVICE_STATUS_OPTIONS = [
    { value: "ENABLED", label: "ENABLED" },
    { value: "DISABLED", label: "DISABLED" },
  ];

  useEffect(() => {
    fetchData();
    fetchPackageList();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const userId = props.userId ? props.userId : props.match.params.userId;

    try {
      const token = AuthService.getAccessToken();
      const response = await UserService.fetchByUserId(token, userId);

      if (response.status === "SUCCESS") {
        const data = response.data.result;
        setUserData(data);

        // Set form data
        setFormData({
          user_type: data.user_type || "",
          domain_name: data.domain_name || "",
          package_id: data.package_id || "",
          name: data.name || "",
          fathers_name: data.fathers_name || "",
          mothers_name: data.mothers_name || "",
          gender: data.gender || "",
          dob: data.dob ? data.dob.split("T")[0] : "",
          mobile: data.mobile || "",
          email: data.email || "",
          alternative_number: data.alternative_number || "",
          whatsapp_number: data.whatsapp_number || "",
          pincode: data.pincode || "",
          state: data.state || "",
          city: data.city || "",
          district: data.district || "",
          address: data.address || "",
          shop_name: data.shop_name || "",
          shop_type: data.shop_type || "",
          nature_of_business: data.nature_of_business || "",
          website_url: data.website_url || "",
          shop_pincode: data.shop_pincode || "",
          shop_state: data.shop_state || "",
          shop_city: data.shop_city || "",
          shop_district: data.shop_district || "",
          shop_address: data.shop_address || "",
          shop_latitude: data.shop_latitude || "",
          shop_longitude: data.shop_longitude || "",
          bank_name: data.bank_name || "",
          account_number: data.account_number || "",
          ifsc_code: data.ifsc_code || "",
          account_type: data.account_type || "",
          pob_type: data.pob_type || "",
          pan_number: data.pan_number || "",
          business_pan: data.business_pan || "",
          gst_number: data.gst_number || "",
          poa_id_number: data.poa_id_number || "",
          status: data.status || "",
          kyc_status: data.kyc_status || "",
          two_fa_status: data.two_fa_status || "",
          wallet_hold: data.wallet_hold || "",
          developer_access: data.developer_access || "",
          nsdlpan_status: data.nsdlpan_status || "",
          utiitslpan_status: data.utiitslpan_status || "",
        });

        // Clear any pending document uploads
        setDocuments({});
      } else {
        Swal.fire("Error!", response.message, "error");
      }
    } catch (error) {
      const errMsg = HttpError.getError(error);
      Swal.fire("Error!", errMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchPackageList = async () => {
    try {
      const token = AuthService.getAccessToken();
      const response = await PackageService.fetchAll(token);

      if (response.status === "SUCCESS") {
        setPackageList(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFormChanged(true);
  };

  const handleDocumentChange = (key, file) => {
    if (!file) return;

    // Validate file size (1MB)
    if (file.size > 1 * 1024 * 1024) {
      Swal.fire(
        "Error!",
        `${DOCUMENT_CONFIG[key].label} size must be less than 1 MB`,
        "error",
      );
      return;
    }

    // Validate file type based on configuration
    const fileExtension = `.${file.name.split(".").pop().toLowerCase()}`;
    const config = DOCUMENT_CONFIG[key];

    if (!config.accept.includes(fileExtension)) {
      Swal.fire(
        "Error!",
        `Only ${config.accept.replace(/,/g, ", ")} files are allowed for ${config.label}`,
        "error",
      );
      return;
    }

    // Store file for upload
    setDocuments((prev) => ({
      ...prev,
      [key]: file,
    }));

    setFormChanged(true);

    // Upload document immediately
    uploadDocumentImmediately(key, file);
  };

  const uploadDocumentImmediately = async (key, file) => {
    try {
      setUploadingDocuments((prev) => ({ ...prev, [key]: true }));

      const token = AuthService.getAccessToken();
      const userId = props.userId ? props.userId : props.match.params.userId;

      const formData = new FormData();
      formData.append("user_id", userId);
      formData.append("key_name", key);
      formData.append("file", file);

      const response = await UserService.uploadKYCDocument(token, formData);

      if (response.status === "SUCCESS") {
        Swal.fire(
          "Success!",
          `${DOCUMENT_CONFIG[key].label} uploaded successfully`,
          "success",
        );

        // Update the document URL for viewing
        setDocumentUrls((prev) => ({
          ...prev,
          [key]: URL.createObjectURL(file), // Temporary local URL for preview
        }));

        // Clear the file from pending documents after successful upload
        setDocuments((prev) => {
          const newDocs = { ...prev };
          delete newDocs[key];
          return newDocs;
        });
      } else {
        Swal.fire(
          "Error!",
          `Failed to upload ${DOCUMENT_CONFIG[key].label}: ${response.message}`,
          "error",
        );
      }
    } catch (error) {
      console.error(`Error uploading ${key}:`, error);
      Swal.fire(
        "Error!",
        `Failed to upload ${DOCUMENT_CONFIG[key].label}`,
        "error",
      );
    } finally {
      setUploadingDocuments((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleViewDocument = async (key) => {
    try {
      const token = AuthService.getAccessToken();
      const userId = props.userId ? props.userId : props.match.params.userId;

      // Call backend API to get document URL or view document
      const response = await UserService.viewDocument(token, userId, key);

      if (response.status === "SUCCESS" && response.data) {
        // If API returns a URL, open it
        if (response.data.url) {
          window.open(response.data.url, "_blank");
        }
        // If API returns base64 or other data format
        else if (response.data.documentData) {
          // Handle base64 or other data formats as needed
          console.log("Document data received:", response.data);
          Swal.fire(
            "Info",
            "Document data received. Please check console for details.",
            "info",
          );
        }
        // If API returns file content directly
        else if (response.data.contentType && response.data.content) {
          // Create a blob and open it
          const byteCharacters = atob(response.data.content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], {
            type: response.data.contentType,
          });
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank");
        }
      } else {
        Swal.fire("Error!", "Failed to load document", "error");
      }
    } catch (error) {
      console.error("Error viewing document:", error);
      Swal.fire("Error!", "Failed to load document", "error");
    }
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.name.trim()) {
      Swal.fire("Error!", "Name is required", "error");
      return;
    }

    if (!formData.mobile || formData.mobile.length !== 10) {
      Swal.fire("Error!", "Valid mobile number is required", "error");
      return;
    }

    if (!formData.email.trim()) {
      Swal.fire("Error!", "Email is required", "error");
      return;
    }

    if (
      formData.user_type === "Reseller" ||
      formData.user_type === "Master Reseller"
    ) {
      if (!formData.domain_name.trim()) {
        Swal.fire(
          "Error!",
          "Domain name is required for Reseller/Master Reseller",
          "error",
        );
        return;
      }
    }

    setLoading(true);

    try {
      const token = AuthService.getAccessToken();
      const userId = props.userId ? props.userId : props.match.params.userId;

      // Update profile
      const updateData = {
        user_id: userId,
        ...formData,
      };

      const updateResponse = await UserService.updateProfile(token, updateData);

      if (updateResponse.status === "SUCCESS") {
        // Check if there are any pending documents that weren't uploaded automatically
        const pendingDocuments = Object.keys(documents);
        if (pendingDocuments.length > 0) {
          // Upload any remaining pending documents
          const uploadPromises = pendingDocuments.map((key) =>
            uploadDocumentImmediately(key, documents[key]),
          );

          await Promise.all(uploadPromises);
        }

        Swal.fire("Success!", "Profile updated successfully", "success");
        setFormChanged(false);
        fetchData(); // Refresh data
      } else {
        Swal.fire("Error!", updateResponse.message, "error");
      }
    } catch (error) {
      const errMsg = HttpError.getError(error);
      Swal.fire("Error!", errMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    if (userData) {
      fetchData(); // Reset to original data
      setDocuments({});
      setDocumentUrls({});
      setFormChanged(false);
      Swal.fire("Reset!", "Form has been reset to original values", "info");
    }
  };

  const renderInputField = (
    label,
    name,
    type = "text",
    options = [],
    extraProps = {},
  ) => {
    const value = formData[name] || "";

    return (
      <div className="form-group">
        <label>{label}</label>
        {type === "select" ? (
          <select
            className="form-control"
            value={value}
            onChange={(e) => handleFormChange(name, e.target.value)}
            {...extraProps}
          >
            <option value=""></option>
            {options.map((option) => (
              <option
                key={option.value || option}
                value={option.value || option}
              >
                {option.label || option}
              </option>
            ))}
          </select>
        ) : type === "textarea" ? (
          <textarea
            className="form-control"
            value={value}
            onChange={(e) => handleFormChange(name, e.target.value)}
            rows={3}
            {...extraProps}
          />
        ) : (
          <input
            type={type}
            className="form-control"
            value={value}
            onChange={(e) => handleFormChange(name, e.target.value)}
            {...extraProps}
          />
        )}
      </div>
    );
  };

  const renderDocumentUpload = (key) => {
    const config = DOCUMENT_CONFIG[key];
    const file = documents[key];
    const existingDocument = userData && userData[key];
    const isUploading = uploadingDocuments[key];
    const previewUrl =
      documentUrls[key] || (file ? URL.createObjectURL(file) : null);

    return (
      <div className="form-group">
        <label>{config.label}</label>
        <div className="input-group">
          <div className="custom-file">
            <input
              type="file"
              className="custom-file-input"
              id={`file-${key}`}
              accept={config.accept}
              onChange={(e) => handleDocumentChange(key, e.target.files[0])}
              disabled={isUploading}
            />
            <label className="custom-file-label" htmlFor={`file-${key}`}>
              {file ? file.name : "Choose file"}
            </label>
          </div>
          {(existingDocument || previewUrl) && (
            <div className="input-group-append">
              <button
                type="button"
                className="btn btn-outline-info"
                onClick={() => {
                  if (previewUrl) {
                    // If we have a preview URL (just uploaded), open it
                    window.open(previewUrl, "_blank");
                  } else if (existingDocument) {
                    // If document exists in database, fetch from backend
                    handleViewDocument(key);
                  }
                }}
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-1"></span>
                    Uploading...
                  </>
                ) : (
                  "View"
                )}
              </button>
            </div>
          )}
        </div>
        <small className="text-muted">
          {config.description} • Max 1MB •{" "}
          {config.accept.replace(/,/g, ", ").toUpperCase()}
        </small>
        {file && !isUploading && (
          <div className="mt-1">
            <span className="badge badge-info">
              Selected: {(file.size / 1024).toFixed(1)} KB
            </span>
          </div>
        )}
        {isUploading && (
          <div className="mt-1">
            <div className="progress" style={{ height: "5px" }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                style={{ width: "100%" }}
              ></div>
            </div>
            <small className="text-info">Uploading...</small>
          </div>
        )}
      </div>
    );
  };

  if (loading && !userData) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-2">Loading user data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body text-center">
              <div className="alert alert-danger">User data not found</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const userId = props.userId ? props.userId : props.match.params.userId;

  return (
    <div className="container-fluid">
      {/* ID and User ID Row */}
      <div className="row mb-3">
        <div className="col-md-12">
          <div className="card card-outline card-primary">
            <div className="card-header">
              <h3 className="card-title">User Information</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-2">
                  <div className="form-group">
                    <label>ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={userData.id || ""}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="form-group">
                    <label>User ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={userId}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <div className="form-group">
                    <label>Referral Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={userData.referral_code || "N/A"}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Referred By</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        userData?.referred_by_details
                          ? `${userData.referred_by_details.name} (${userData.referred_by_details.user_id})`
                          : "N/A"
                      }
                      readOnly
                      disabled
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Parent</label>
                    <input
                      type="text"
                      className="form-control"
                      value={
                        userData?.parent_detail
                          ? `${userData.parent_detail.name} (${userData.parent_detail.user_id})`
                          : "N/A"
                      }
                      readOnly
                      disabled
                    />
                    <small className=" text-muted">
                      {userData?.parent_detail
                        ? `${userData.parent_detail.user_type}`
                        : ""}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <form autoComplete="off">
        {/* Row 1: Basic Information & KYC Information */}
        <div className="row">
          {/* Basic Information - Left Column */}
          <div className="col-md-6">
            <div className="card card-outline card-primary">
              <div className="card-header">
                <h3 className="card-title">Basic Information</h3>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    {renderInputField(
                      "User Type",
                      "user_type",
                      "select",
                      USER_TYPE_OPTIONS,
                    )}
                  </div>
                  <div className="col-md-6">
                    {renderInputField(
                      "Gender",
                      "gender",
                      "select",
                      GENDER_OPTIONS,
                    )}
                  </div>
                </div>

                {(formData.user_type === "Reseller" ||
                  formData.user_type === "Master Reseller") && (
                  <div className="row">
                    <div className="col-md-12">
                      {renderInputField(
                        "Domain Name",
                        "domain_name",
                        "text",
                        [],
                        {
                          placeholder: "example.com",
                        },
                      )}
                    </div>
                  </div>
                )}

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField("Name", "name", "text", [], {
                      placeholder: "Full Name",
                    })}
                  </div>
                  <div className="col-md-6">
                    {renderInputField("Date of Birth", "dob", "date")}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField(
                      "Father's Name",
                      "fathers_name",
                      "text",
                      [],
                      {
                        placeholder: "Father's Name",
                      },
                    )}
                  </div>
                  <div className="col-md-6">
                    {renderInputField(
                      "Mother's Name",
                      "mothers_name",
                      "text",
                      [],
                      {
                        placeholder: "Mother's Name",
                      },
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField("Mobile Number", "mobile", "tel", [], {
                      maxLength: 10,
                      placeholder: "10-digit mobile number",
                    })}
                  </div>
                  <div className="col-md-6">
                    {renderInputField("Email", "email", "email", [], {
                      placeholder: "email@example.com",
                    })}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField(
                      "Alternative Number",
                      "alternative_number",
                      "tel",
                      [],
                      {
                        maxLength: 10,
                        placeholder: "Optional alternative number",
                      },
                    )}
                  </div>
                  <div className="col-md-6">
                    {renderInputField(
                      "WhatsApp Number",
                      "whatsapp_number",
                      "tel",
                      [],
                      {
                        maxLength: 10,
                        placeholder: "Optional WhatsApp number",
                      },
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField("PIN Code", "pincode", "text", [], {
                      maxLength: 6,
                      placeholder: "6-digit PIN code",
                    })}
                  </div>
                  <div className="col-md-6">
                    {renderInputField("State", "state", "select", states)}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField("City", "city", "text", [], {
                      placeholder: "City",
                    })}
                  </div>
                  <div className="col-md-6">
                    {renderInputField("District", "district", "text", [], {
                      placeholder: "District",
                    })}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    {renderInputField("Address", "address", "textarea", [], {
                      placeholder: "Full address",
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* KYC Information - Right Column */}
          <div className="col-md-6">
            <div className="card card-outline card-info">
              <div className="card-header">
                <h3 className="card-title">KYC Information</h3>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    {renderInputField("PAN Number", "pan_number", "text", [], {
                      maxLength: 10,
                      placeholder: "10-character PAN",
                      style: { textTransform: "uppercase" },
                    })}
                  </div>
                  <div className="col-md-6">
                    {renderInputField(
                      "Aadhaar Number",
                      "poa_id_number",
                      "text",
                      [],
                      {
                        maxLength: 12,
                        placeholder: "12-digit Aadhaar",
                      },
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField(
                      "Business PAN",
                      "business_pan",
                      "text",
                      [],
                      {
                        maxLength: 10,
                        placeholder: "Business PAN",
                        style: { textTransform: "uppercase" },
                      },
                    )}
                  </div>
                  <div className="col-md-6">
                    {renderInputField("GST Number", "gst_number", "text", [], {
                      placeholder: "GST number",
                      style: { textTransform: "uppercase" },
                    })}
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-12">
                    <div className="card card-secondary">
                      <div className="card-header">
                        <h3 className="card-title">KYC Documents</h3>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            {renderDocumentUpload("profile_img")}
                            {renderDocumentUpload("poa_frontside_img")}
                            {renderDocumentUpload("poa_backside_img")}
                          </div>
                          <div className="col-md-6">
                            {renderDocumentUpload("pan_img")}
                            {renderDocumentUpload("gst_img")}
                            {renderDocumentUpload("business_pan_img")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Shop Information & Bank Information */}
        <div className="row mt-3">
          {/* Shop Information - Left Column */}
          <div className="col-md-6">
            <div className="card card-outline card-warning">
              <div className="card-header">
                <h3 className="card-title">Shop/Business Information</h3>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    {renderInputField(
                      "Business/Shop Name",
                      "shop_name",
                      "text",
                      [],
                      {
                        placeholder: "Business/Shop name",
                      },
                    )}
                  </div>
                  <div className="col-md-6">
                    {renderInputField(
                      "Shop Type",
                      "shop_type",
                      "select",
                      SHOP_TYPE_OPTIONS,
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField(
                      "Nature of Business",
                      "nature_of_business",
                      "text",
                      [],
                      {
                        placeholder: "Nature of business",
                      },
                    )}
                  </div>
                  <div className="col-md-6">
                    {renderInputField(
                      "Website URL",
                      "website_url",
                      "text",
                      [],
                      {
                        placeholder: "https://example.com",
                      },
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField(
                      "Shop PIN Code",
                      "shop_pincode",
                      "text",
                      [],
                      {
                        maxLength: 6,
                        placeholder: "6-digit PIN code",
                      },
                    )}
                  </div>
                  <div className="col-md-6">
                    {renderInputField(
                      "Shop State",
                      "shop_state",
                      "select",
                      states,
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField("Shop City", "shop_city", "text", [], {
                      placeholder: "Shop city",
                    })}
                  </div>
                  <div className="col-md-6">
                    {renderInputField(
                      "Shop District",
                      "shop_district",
                      "text",
                      [],
                      {
                        placeholder: "Shop district",
                      },
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField(
                      "Shop Latitude",
                      "shop_latitude",
                      "text",
                      [],
                      {
                        placeholder: "Latitude coordinates",
                      },
                    )}
                  </div>
                  <div className="col-md-6">
                    {renderInputField(
                      "Shop Longitude",
                      "shop_longitude",
                      "text",
                      [],
                      {
                        placeholder: "Longitude coordinates",
                      },
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    {renderInputField(
                      "Shop Address",
                      "shop_address",
                      "textarea",
                      [],
                      {
                        placeholder: "Shop/business address",
                        rows: 2,
                      },
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bank Information - Right Column */}
          <div className="col-md-6">
            <div className="card card-outline card-success">
              <div className="card-header">
                <h3 className="card-title">Bank Information</h3>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    {renderInputField("Bank Name", "bank_name", "text", [], {
                      placeholder: "Bank name",
                      style: { textTransform: "uppercase" },
                    })}
                  </div>
                  <div className="col-md-6">
                    {renderInputField(
                      "Account Number",
                      "account_number",
                      "text",
                      [],
                      {
                        placeholder: "Bank account number",
                      },
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField("IFSC Code", "ifsc_code", "text", [], {
                      placeholder: "IFSC code",
                      style: { textTransform: "uppercase" },
                    })}
                  </div>
                  <div className="col-md-6">
                    {renderInputField(
                      "Account Type",
                      "account_type",
                      "select",
                      ACCOUNT_TYPE_OPTIONS,
                    )}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    {renderInputField(
                      "Proof of Bank Type",
                      "pob_type",
                      "select",
                      POB_TYPE_OPTIONS,
                    )}
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-12">
                    <div className="card card-secondary">
                      <div className="card-header">
                        <h3 className="card-title">Bank Documents</h3>
                      </div>
                      <div className="card-body">
                        {renderDocumentUpload("pob_img")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 3: Status & Settings - Full Width */}
        <div className="row mt-3">
          <div className="col-md-12">
            <div className="card card-outline card-dark">
              <div className="card-header">
                <h3 className="card-title">Status & Settings</h3>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Package</label>
                      <select
                        className="form-control"
                        value={formData.package_id}
                        onChange={(e) =>
                          handleFormChange("package_id", e.target.value)
                        }
                      >
                        <option value="">Select Package</option>
                        {packageList.map((pkg) => (
                          <option key={pkg.id} value={pkg.id}>
                            {pkg.name}
                          </option>
                        ))}
                      </select>
                      <small className=" text-muted">
                        Default package will be auto-applied if not selected.
                      </small>
                    </div>
                  </div>
                  <div className="col-md-3">
                    {renderInputField(
                      "Status",
                      "status",
                      "select",
                      STATUS_OPTIONS,
                    )}
                  </div>
                  <div className="col-md-3">
                    {renderInputField(
                      "KYC Status",
                      "kyc_status",
                      "select",
                      KYC_STATUS_OPTIONS,
                    )}
                  </div>
                  <div className="col-md-3">
                    {renderInputField(
                      "2FA Status (Enabled By Default)",
                      "two_fa_status",
                      "select",
                      TWO_FA_OPTIONS,
                    )}
                  </div>
                </div>

                <div className="row mt-2">
                  <div className="col-md-3">
                    {renderInputField(
                      "Wallet Hold",
                      "wallet_hold",
                      "select",
                      WALLET_HOLD_OPTIONS,
                    )}
                  </div>
                  <div className="col-md-3">
                    {renderInputField(
                      "Developer Access",
                      "developer_access",
                      "select",
                      DEVELOPER_ACCESS_OPTIONS,
                    )}
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Registered Date</label>
                      <input
                        type="text"
                        className="form-control"
                        value={userData.created_on || ""}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-group">
                      <label>Last Update Date</label>
                      <input
                        type="text"
                        className="form-control"
                        value={userData.modified_on || ""}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>
                </div>

                <div className="row mt-2">
                  <div className="col-md-3">
                    {renderInputField(
                      "NSDL PAN Status",
                      "nsdlpan_status",
                      "select",
                      SERVICE_STATUS_OPTIONS,
                    )}
                  </div>
                  <div className="col-md-3">
                    {renderInputField(
                      "UTI PAN Status",
                      "utiitslpan_status",
                      "select",
                      SERVICE_STATUS_OPTIONS,
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 4: Action Buttons - Full Width */}
        <div className="row mt-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-footer">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    {formChanged && (
                      <span className="badge badge-warning">
                        <i className="fas fa-exclamation-circle mr-1"></i>
                        You have unsaved changes
                      </span>
                    )}
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-default mr-2"
                      onClick={handleReset}
                      disabled={loading}
                    >
                      <i className="fas fa-redo mr-1"></i>
                      Reset
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSubmit}
                      disabled={loading || !formChanged}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm mr-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save mr-1"></i>
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditUserForm;
